import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import Button from '@raydiant/raydial/components/Button';
import Text from '@raydiant/raydial/components/Text';
import PublishIcon from '@material-ui/icons/Publish';
import { Device } from '@raydiant/api-client-js';
import {
  selectDeviceStatusById,
  selectPlaylistStatusByDeviceIdBulkUpdate,
} from '../../../../selectors/v2/devices';
import {
  selectProofOfPublishEnabled,
  selectUserProfile,
} from '../../../../selectors/user';
import {
  canPublishLocationDevice,
  getDevicePublishPlaybackStatus,
  isDevicePlaybackEventsSupported,
  isDevicePublishable,
} from '../../../../utilities';
import Spinner from '@raydiant/raydial/components/Spinner';
import CircleRotateLeft from '../../Icons/CircleRotateLeft';
import CircleCheck from '../../Icons/CircleCheck';

export interface PublishButtonProps {
  className?: string;
  device: Device;
  isOnline: boolean;
  isBatchOperationInProgress: boolean;
  onClick: () => void;
}

const PublishButton = ({
  className,
  device,
  isOnline,
  isBatchOperationInProgress,
  onClick,
}: PublishButtonProps) => {
  // Selectors

  const deviceStatusById = useSelector(selectDeviceStatusById);
  const playlistStatusByDeviceId = useSelector(
    selectPlaylistStatusByDeviceIdBulkUpdate,
  );
  // TODO: Remove when we release proof of publish to all users.
  const isProofOfPublishEnabled = useSelector(selectProofOfPublishEnabled);
  const currentUser = useSelector(selectUserProfile);

  const deviceStatus = deviceStatusById[device.id];
  const playlistStatus = playlistStatusByDeviceId[device.id];
  const publishPlaybackStatus = getDevicePublishPlaybackStatus(device);

  // State

  const [publishStatus, setPublishStatus] = useState<
    '' | 'pending' | 'received' | 'success' | 'queued'
  >('');

  // Effects

  const deviceSupportsProofOfPlayback = isDevicePlaybackEventsSupported(device);
  const isRequestingPublish = deviceStatus === 'publishing';
  const isWaitingForPublishAck = publishPlaybackStatus === 'waitingForAck';
  const didReceivePublishAck = publishPlaybackStatus === 'ackReceived';
  // If the device does not support proof of playback (Lite and SecondScreen), there is
  // no publish playback status so we can only use isRequestingPublish to determine
  // if it's complete.
  const didPublishComplete = deviceSupportsProofOfPlayback
    ? publishPlaybackStatus === 'playbackComplete'
    : !isRequestingPublish;

  const prevDidPublishComplete = useRef<boolean>(didPublishComplete);
  const prevIsOnline = useRef<boolean>(isOnline);
  // Update publish status UI state.
  useEffect(() => {
    if (!isBatchOperationInProgress) {
      let timeout: ReturnType<typeof setTimeout>;

      if (isProofOfPublishEnabled) {
        if (isRequestingPublish || isWaitingForPublishAck) {
          setPublishStatus(isOnline ? 'pending' : 'queued');
        } else if (didReceivePublishAck) {
          setPublishStatus(isOnline ? 'received' : 'queued');
        } else if (didPublishComplete) {
          // Show 'success' if device is online and didPublishComplete changes
          // from false to true.
          if (prevDidPublishComplete.current === false) {
            timeout = setTimeout(() => {
              setPublishStatus('success');
              timeout = setTimeout(() => {
                setPublishStatus('');
              }, 9000); // Published! should remain visible for 9 seconds
            }, 1000); // Publishing... should remain visible for at least 1 second
          }
        }
      } else {
        setPublishStatus(isRequestingPublish ? 'pending' : '');
      }

      // Reset status if device changes from online to offline and we're no
      // longer publishing. This is required to remove the queued state from
      // Lite and SecondScreen when they come back online since they currently
      // do not implment playback events.
      if (!prevIsOnline.current && isOnline) {
        setPublishStatus('');
      }

      prevDidPublishComplete.current = didPublishComplete;
      prevIsOnline.current = isOnline;

      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }
  }, [
    isBatchOperationInProgress,
    isRequestingPublish,
    isWaitingForPublishAck,
    didReceivePublishAck,
    didPublishComplete,
    isOnline,
    isProofOfPublishEnabled,
    deviceSupportsProofOfPlayback,
  ]);

  // Render

  const isPublishDisabled =
    playlistStatus === 'pendingUpload' ||
    playlistStatus === 'uploadError' ||
    (!isProofOfPublishEnabled && isRequestingPublish) ||
    !(currentUser && canPublishLocationDevice(currentUser, device));

  const isPublishable = isDevicePublishable(
    device,
    deviceStatus,
    playlistStatus,
  );

  if (isBatchOperationInProgress) {
    return (
      <div className={className}>
        <div className={cn('text-gray-600 flex items-center')}>
          <div className="hidden md:block">
            <Text variant="caption">Applying changes ...</Text>
          </div>
          <Spinner className="ml-3 mb-1 text-gray-600 md:mb-0" size="small" />
        </div>
      </div>
    );
  }

  if (isPublishable || (!isProofOfPublishEnabled && isRequestingPublish)) {
    return (
      <div className={className}>
        <div className="hidden md:block">
          <Button
            className="w-[132px] h-10"
            variant="primary"
            onPress={onClick}
            isDisabled={isPublishDisabled}
          >
            Publish
          </Button>
        </div>
        <div className="block md:hidden">
          <Button
            className={cn('bg-blue-50 p-2 rounded-lg ml-3', className)}
            variant="unstyled"
            onPress={onClick}
            isDisabled={isPublishDisabled}
          >
            {/* this is for the mobile and current design do not support mobile view */}
            <PublishIcon className="text-blue-600" />
          </Button>
        </div>
      </div>
    );
  }

  if (publishStatus === 'queued') {
    return (
      <div className={className}>
        <div className={cn('text-gray-600 flex items-center')}>
          <div className="hidden md:block">
            <Text variant="caption">Publish pending</Text>
          </div>
          <CircleRotateLeft className="ml-3 mb-1 text-gray-600 md:mb-0" />
        </div>
      </div>
    );
  }

  if (publishStatus === 'pending' || publishStatus === 'received') {
    return (
      <div className={className}>
        <div className={cn('text-gray-600 flex items-center')}>
          <div className="hidden md:block">
            <Text variant="caption">
              {publishStatus === 'pending'
                ? 'Publishing...'
                : 'Publish received'}
            </Text>
          </div>
          <Spinner className="ml-3 mb-1 text-gray-600 md:mb-0" size="small" />
        </div>
      </div>
    );
  }

  if (publishStatus === 'success') {
    return (
      <div className={className}>
        <div className={cn('text-gray-600 flex items-center')}>
          <div className="hidden md:block">
            <Text variant="caption">Published!</Text>
          </div>
          <CircleCheck className="ml-3 mb-1 !text-green-200 md:mb-0" />
        </div>
      </div>
    );
  }

  return null;
};

export default PublishButton;
